/**
 * Users List Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Grid/list of user cards with loading and empty states.
 */

import React from 'react';
import { Users } from 'lucide-react';
import type { UserListItem } from '../../../types/admin';
import UserCard from './UserCard';

interface UsersListProps {
  users: UserListItem[];
  isLoading?: boolean;
  currentUserId?: string;
  onEdit: (user: UserListItem) => void;
  onChangeRole: (user: UserListItem) => void;
  onToggleStatus: (user: UserListItem) => void;
  onDelete: (user: UserListItem) => void;
  onResendInvite?: (user: UserListItem) => void;
  onResetPassword?: (user: UserListItem) => void;
  onManageClients?: (user: UserListItem) => void;
}

// Skeleton loader for user cards
const UserCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-200" />
        <div>
          <div className="h-4 w-32 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-40 bg-gray-200 rounded" />
        </div>
      </div>
      <div className="w-6 h-6 bg-gray-200 rounded" />
    </div>
    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
      <div className="h-3 w-24 bg-gray-200 rounded" />
      <div className="h-3 w-28 bg-gray-200 rounded" />
    </div>
  </div>
);

const UsersList: React.FC<UsersListProps> = ({
  users,
  isLoading = false,
  currentUserId,
  onEdit,
  onChangeRole,
  onToggleStatus,
  onDelete,
  onResendInvite,
  onResetPassword,
  onManageClients,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <UserCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Empty state
  if (users.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Users size={48} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No users found</h3>
        <p className="text-gray-500">
          Try adjusting your search or filters, or invite a new user.
        </p>
      </div>
    );
  }

  // User grid
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {users.map((user) => (
        <UserCard
          key={user.id}
          user={user}
          currentUserId={currentUserId}
          onEdit={onEdit}
          onChangeRole={onChangeRole}
          onToggleStatus={onToggleStatus}
          onDelete={onDelete}
          onResendInvite={onResendInvite}
          onResetPassword={onResetPassword}
          onManageClients={onManageClients}
        />
      ))}
    </div>
  );
};

export default UsersList;
